/**
 * Sankey graph builder for the accounting overview. Turns the flat
 * commit list into {nodes, links} for whichever left/right axis pair is
 * selected:
 *
 *   left  ∈ { business function | team (user-defined engineer teams) }
 *   right ∈ { investment area (KTLO/Enablement/Growth)
 *           | treatment (CapEx / OpEx / OpEx-R&D) }
 *
 * Dollars flow left → right, summed per (left, right) pair. Only nodes
 * that carry value are emitted, so an empty bucket never renders a
 * zero-height bar (which d3-sankey would otherwise lay out as a sliver).
 */

import type { TeamGrouping } from '../../team-filter';

import {
  TREATMENT_NODE_LABELS,
  treatmentRowFor,
  type TreatmentRow,
} from './rollups';
import { INVESTMENT_LABELS } from './rollups';
import type { AccountingView, InvestmentType } from './types';

export type LeftAxis = 'function' | 'team';
export type RightAxis = 'investment' | 'treatment';

/**
 * `category` keys the colour map in the renderer:
 *   investment → ktlo | enablement | growth
 *   treatment  → opex_rd | opex_other | capex
 *   left nodes → 'function' | 'team'
 */
export interface SankeyNodeDatum {
  id: string;
  label: string;
  side: 'left' | 'right';
  category: string;
}

export interface SankeyLinkDatum {
  source: string;
  target: string;
  value: number;
}

export interface SankeyGraphData {
  nodes: SankeyNodeDatum[];
  links: SankeyLinkDatum[];
}

const INVESTMENT_ORDER: InvestmentType[] = ['ktlo', 'enablement', 'growth'];
const TREATMENT_ORDER: TreatmentRow[] = ['opex_rd', 'opex_other', 'capex'];

export function buildSankeyGraph(
  view: AccountingView,
  left: LeftAxis,
  right: RightAxis,
  teams: TeamGrouping,
): SankeyGraphData {
  const bfById = new Map(view.business_functions.map((bf) => [bf.id, bf]));

  // Per-key display labels, captured as we encounter live keys so node
  // ordering follows a stable, meaningful sequence rather than Map order.
  const leftSeen = new Map<string, string>(); // id → label
  const rightSeen = new Set<string>();
  const linkTotals = new Map<string, number>(); // `${leftId} ${rightId}` → $

  for (const c of view.commits) {
    // ---- left node id + label ----
    let leftId: string;
    let leftLabel: string;
    if (left === 'team') {
      const key = teams.keyOf(c.author_engineer_id);
      leftId = `L:team:${key}`;
      leftLabel = teams.labelOf(key);
    } else {
      const bf = bfById.get(c.business_function_id);
      leftId = `L:bf:${c.business_function_id}`;
      leftLabel = bf?.display ?? 'Unattributed';
    }
    leftSeen.set(leftId, leftLabel);

    // ---- right node id ----
    let rightId: string;
    if (right === 'investment') {
      rightId = `R:inv:${c.decision.investment_type}`;
    } else {
      const row = treatmentRowFor(c.decision.accounting_treatment, c.decision.opex_subtype);
      rightId = `R:tr:${row}`;
    }
    rightSeen.add(rightId);

    const key = `${leftId} ${rightId}`;
    linkTotals.set(key, (linkTotals.get(key) ?? 0) + c.cost_usd);
  }

  // ---- ordered left nodes ----
  const leftNodes: SankeyNodeDatum[] = [];
  if (left === 'team') {
    for (const key of teams.order) {
      const id = `L:team:${key}`;
      if (leftSeen.has(id)) {
        leftNodes.push({ id, label: teams.labelOf(key), side: 'left', category: 'team' });
      }
    }
  } else {
    // Business functions in catalog order, then any stragglers.
    for (const bf of view.business_functions) {
      const id = `L:bf:${bf.id}`;
      if (leftSeen.has(id)) {
        leftNodes.push({ id, label: bf.display, side: 'left', category: 'function' });
      }
    }
    for (const [id, label] of leftSeen) {
      if (!leftNodes.some((n) => n.id === id)) {
        leftNodes.push({ id, label, side: 'left', category: 'function' });
      }
    }
  }

  // ---- ordered right nodes ----
  const rightNodes: SankeyNodeDatum[] = [];
  if (right === 'investment') {
    for (const inv of INVESTMENT_ORDER) {
      const id = `R:inv:${inv}`;
      if (rightSeen.has(id)) {
        rightNodes.push({ id, label: INVESTMENT_LABELS[inv], side: 'right', category: inv });
      }
    }
  } else {
    for (const row of TREATMENT_ORDER) {
      const id = `R:tr:${row}`;
      if (rightSeen.has(id)) {
        rightNodes.push({ id, label: TREATMENT_NODE_LABELS[row], side: 'right', category: row });
      }
    }
  }

  const links: SankeyLinkDatum[] = [];
  for (const [key, value] of linkTotals) {
    if (value <= 0) continue;
    const [source, target] = key.split(' ');
    links.push({ source: source!, target: target!, value });
  }

  return { nodes: [...leftNodes, ...rightNodes], links };
}

/**
 * Brand colours per right-axis category — mirrors the table row bands.
 * Investment areas reuse the Timeline snapshot-notes indicator palette
 * (green/purple) with a matching-saturation red for KTLO.
 */
export const CATEGORY_COLORS: Record<string, string> = {
  ktlo: '#e5544e', // red
  enablement: '#86db5a', // timeline "skill" green
  growth: '#8b5cf6', // timeline "team" purple
  // Treatment reuses the same red / green / purple.
  opex_other: '#e5544e', // OpEx → red
  opex_rd: '#86db5a', // OpEx — R&D → green
  capex: '#8b5cf6', // CapEx → purple
};
